/**
 * Product Card Video Controller
 * Handles autoplay functionality and accessible video controls for product cards with carousel integration
 */

// Prevent multiple declarations
if (typeof window.ProductCardVideo !== 'undefined') {
  console.warn('ProductCardVideo already exists, skipping redefinition');
} else {

class ProductCardVideo {
  constructor() {
    this.intersectionObserver = null;
    this.activeVideos = new Set();
    this.observedCards = new WeakSet();
    // Track videos that were manually paused by user - don't auto-replay these
    this.userPausedVideos = new WeakSet();
    this.init();
  }

  // Helper method to safely get closest element
  #getClosestElement(target, selector) {
    if (!target || typeof target.closest !== 'function') return null;
    return target.closest(selector);
  }

  init() {
    this.#setupIntersectionObserver();
    this.#setupVideoControls();
    this.#setupScrollListener();
    
    // Initialize immediately if DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.#observeProductCards();
        setTimeout(() => this.checkInitialVideoStates(), 500);
      });
    } else {
      this.#observeProductCards();
      setTimeout(() => this.checkInitialVideoStates(), 500);
    }

    // Hook into existing ProductCard system
    this.#hookIntoProductCards();
  }

  #hookIntoProductCards() {
    // Use MutationObserver to catch dynamically added product cards
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.tagName === 'PRODUCT-CARD') {
              this.#observeProductCard(node);
            } else if (node.querySelectorAll) {
              const productCards = node.querySelectorAll('product-card');
              productCards.forEach(card => this.#observeProductCard(card));
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  #observeProductCards() {
    const productCards = document.querySelectorAll('product-card');
    productCards.forEach(card => this.#observeProductCard(card));
  }

  #observeProductCard(productCard) {
    if (this.observedCards.has(productCard)) return;
    this.observedCards.add(productCard);

    // Wait for the ProductCard to initialize its carousel
    const waitForCarousel = () => {
      if (productCard.embla) {
        this.#attachToExistingCarousel(productCard);
      } else {
        // Check again after a delay
        setTimeout(waitForCarousel, 200);
      }
    };

    // Start checking immediately and continue until found
    waitForCarousel();
  }

  #attachToExistingCarousel(productCard) {
    if (productCard.dataset.videoAttached) return;
    productCard.dataset.videoAttached = 'true';

    // Hook into the existing embla select event
    const originalOnSelect = productCard.embla.on;
    
    // Add our video handling to the existing select event
    productCard.embla.on('select', () => {
      this.#handleSlideChange(productCard);
    });

    // Initial check for current slide
    setTimeout(() => {
      this.#handleSlideChange(productCard);
    }, 100);
  }

  #setupIntersectionObserver() {
    // Observe product cards for viewport visibility
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const productCard = entry.target;
        
        if (!entry.isIntersecting) {
          // Card not visible - pause all videos (system pause, not user pause)
          this.#pauseAllVideosInCard(productCard, false);
        } else {
          // Card visible - check for video autoplay
          setTimeout(() => {
            this.#handleCardInView(productCard);
          }, 100);
        }
      });
    }, {
      rootMargin: '50px',
      threshold: 0.3
    });

    // Observe existing cards
    document.querySelectorAll('product-card').forEach(card => {
      this.intersectionObserver.observe(card);
    });
  }

  #setupScrollListener() {
    let scrollTimeout = null;
    
    const checkVideoVisibility = () => {
      document.querySelectorAll('product-card').forEach(productCard => {
        const isInView = this.#isCardInView(productCard);
        
        if (!isInView) {
          // System pause when scrolled out of view
          this.#pauseAllVideosInCard(productCard, false);
        } else if (productCard.embla) {
          const currentSlideIndex = productCard.embla.selectedScrollSnap();
          const slides = productCard.querySelectorAll('.embla__slide');
          const currentSlide = slides[currentSlideIndex];
          
          if (currentSlide && currentSlide.dataset.mediaType === 'video') {
            const video = currentSlide.querySelector('.product-card__video');
            // Only autoplay if video wasn't manually paused by user
            if (video && video.paused && !this.userPausedVideos.has(video)) {
              this.#playVideo(video);
            }
          }
        }
      });
    };

    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(checkVideoVisibility, 150);
    }, { passive: true });

    window.addEventListener('resize', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(checkVideoVisibility, 150);
    }, { passive: true });
  }

  #setupVideoControls() {
    // Video control interactions
    document.addEventListener('click', (e) => {
      const button = this.#getClosestElement(e.target, '.product-card__video-toggle');
      if (button) {
        const videoContainer = button.closest('.product-card__video-container');
        const video = videoContainer?.querySelector('.product-card__video');
        if (video) {
          // This is a user-initiated action
          this.#toggleVideo(video, button, true);
        }
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        const button = this.#getClosestElement(e.target, '.product-card__video-toggle');
        if (button) {
          e.preventDefault();
          const videoContainer = button.closest('.product-card__video-container');
          const video = videoContainer?.querySelector('.product-card__video');
          if (video) {
            // This is a user-initiated action
            this.#toggleVideo(video, button, true);
          }
        }
      }
    });

    // Show/hide controls on interaction
    document.addEventListener('mouseenter', (e) => {
      const videoContainer = this.#getClosestElement(e.target, '.product-card__video-container');
      if (videoContainer) {
        this.#showControls(videoContainer);
      }
    }, true);

    document.addEventListener('mouseleave', (e) => {
      const videoContainer = this.#getClosestElement(e.target, '.product-card__video-container');
      if (videoContainer) {
        this.#hideControls(videoContainer);
      }
    }, true);

    document.addEventListener('focusin', (e) => {
      const videoContainer = this.#getClosestElement(e.target, '.product-card__video-container');
      if (videoContainer) {
        this.#showControls(videoContainer);
      }
    });

    document.addEventListener('focusout', (e) => {
      const videoContainer = this.#getClosestElement(e.target, '.product-card__video-container');
      const relatedTarget = e.relatedTarget;
      
      if (videoContainer && (!relatedTarget || !videoContainer.contains(relatedTarget))) {
        this.#hideControls(videoContainer);
      }
    });
  }

  #handleSlideChange(productCard) {
    if (!productCard.embla) return;

    // Pause all videos in this card first (system pause, not user pause)
    this.#pauseAllVideosInCard(productCard, false);

    // Get current slide
    const currentSlideIndex = productCard.embla.selectedScrollSnap();
    const slides = productCard.querySelectorAll('.embla__slide');
    const currentSlide = slides[currentSlideIndex];

    // Check if current slide has video and card is in view
    if (currentSlide && currentSlide.dataset.mediaType === 'video') {
      const video = currentSlide.querySelector('.product-card__video');
      // Only autoplay if video wasn't manually paused by user and card is in view
      if (video && this.#isCardInView(productCard) && !this.userPausedVideos.has(video)) {
        // Small delay for smooth transition
        setTimeout(() => {
          if (video.paused && !this.userPausedVideos.has(video)) {
            this.#playVideo(video);
          }
        }, 150);
      }
    }
  }

  #handleCardInView(productCard) {
    if (!this.#isCardInView(productCard) || !productCard.embla) return;

    const currentSlideIndex = productCard.embla.selectedScrollSnap();
    const slides = productCard.querySelectorAll('.embla__slide');
    const currentSlide = slides[currentSlideIndex];

    if (currentSlide && currentSlide.dataset.mediaType === 'video') {
      const video = currentSlide.querySelector('.product-card__video');
      // Only autoplay if video wasn't manually paused by user
      if (video && video.paused && !this.userPausedVideos.has(video)) {
        this.#playVideo(video);
      }
    }
  }

  #isCardInView(productCard) {
    const rect = productCard.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
    const visibleWidth = Math.min(rect.right, windowWidth) - Math.max(rect.left, 0);
    
    const visibleArea = Math.max(0, visibleHeight) * Math.max(0, visibleWidth);
    const totalArea = rect.height * rect.width;
    const visibilityRatio = totalArea > 0 ? visibleArea / totalArea : 0;

    return visibilityRatio > 0.5;
  }

  checkInitialVideoStates() {
    document.querySelectorAll('product-card').forEach(productCard => {
      if (this.#isCardInView(productCard) && productCard.embla) {
        const currentSlideIndex = productCard.embla.selectedScrollSnap();
        const slides = productCard.querySelectorAll('.embla__slide');
        const currentSlide = slides[currentSlideIndex];
        
        if (currentSlide && currentSlide.dataset.mediaType === 'video') {
          const video = currentSlide.querySelector('.product-card__video');
          // Only autoplay if video wasn't manually paused by user
          if (video && video.paused && !this.userPausedVideos.has(video)) {
            this.#playVideo(video);
          }
        }
      }
    });
  }

  #playVideo(video) {
    if (!video || !video.paused) return;

    video.play().then(() => {
      this.activeVideos.add(video);
      const button = video.parentElement.querySelector('.product-card__video-toggle');
      this.#updateButtonState(button, true);
    }).catch(error => {
      console.warn('Video autoplay failed:', error);
    });
  }

  #pauseVideo(video, isUserAction = false) {
    if (!video || video.paused) return;

    video.pause();
    this.activeVideos.delete(video);
    
    // Track if this was a user-initiated pause
    if (isUserAction) {
      this.userPausedVideos.add(video);
    }
    
    const button = video.parentElement.querySelector('.product-card__video-toggle');
    this.#updateButtonState(button, false);
  }

  #toggleVideo(video, button, isUserAction = false) {
    if (video.paused) {
      // User wants to play - remove from user-paused list
      if (isUserAction) {
        this.userPausedVideos.delete(video);
      }
      this.#playVideo(video);
    } else {
      // User wants to pause - mark as user-paused if this is a user action
      this.#pauseVideo(video, isUserAction);
    }
  }

  #pauseAllVideosInCard(productCard, isUserAction = false) {
    const videos = productCard.querySelectorAll('.product-card__video');
    videos.forEach(video => this.#pauseVideo(video, isUserAction));
  }

  #updateButtonState(button, isPlaying) {
    if (!button) return;

    const playIcon = button.querySelector('.product-card__video-play-icon');
    const pauseIcon = button.querySelector('.product-card__video-pause-icon');
    
    if (isPlaying) {
      button.setAttribute('aria-label', button.dataset.pauseLabel || 'Pause video');
      if (playIcon) playIcon.style.display = 'none';
      if (pauseIcon) pauseIcon.style.display = 'block';
    } else {
      button.setAttribute('aria-label', button.dataset.playLabel || 'Play video');
      if (playIcon) playIcon.style.display = 'block';
      if (pauseIcon) pauseIcon.style.display = 'none';
    }
  }

  #showControls(videoContainer) {
    const controls = videoContainer.querySelector('.product-card__video-controls');
    if (controls) {
      controls.classList.add('product-card__video-controls--visible');
    }
  }

  #hideControls(videoContainer) {
    const controls = videoContainer.querySelector('.product-card__video-controls');
    if (controls) {
      controls.classList.remove('product-card__video-controls--visible');
    }
  }

  // Public methods
  pauseAllVideos() {
    this.activeVideos.forEach(video => this.#pauseVideo(video, false));
  }

  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.pauseAllVideos();
    this.activeVideos.clear();
    // Clear user pause tracking
    this.userPausedVideos = new WeakSet();
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productCardVideo = new ProductCardVideo();
  });
} else {
  window.productCardVideo = new ProductCardVideo();
}

// Handle page visibility for performance
document.addEventListener('visibilitychange', () => {
  if (window.productCardVideo && document.hidden) {
    window.productCardVideo.pauseAllVideos();
  }
});

} // End of ProductCardVideo definition block 